import { STORAGE_KEYS } from '@/config/api.config';

/**
 * Whether the user asked to stay signed in. Always kept in localStorage:
 * it is the preference itself, not session data.
 */
export const REMEMBER_KEY = 'auth_remember';

/**
 * Email prefilled on the login form when "Se souvenir de moi" is checked.
 */
export const REMEMBERED_EMAIL_KEY = 'auth_remembered_email';

/**
 * Keys that follow the "Se souvenir de moi" choice: localStorage when the box
 * is checked (the session survives a browser restart), sessionStorage when it
 * is not (the session dies with the tab).
 */
const SESSION_KEYS = [
  STORAGE_KEYS.JWT,
  STORAGE_KEYS.REFRESH_TOKEN,
  STORAGE_KEYS.USER,
  STORAGE_KEYS.ROLE,
  STORAGE_KEYS.REDIRECT_AFTER_LOGIN,
];

/**
 * Storage access throws in private browsing and when cookies are blocked.
 * Every read and write goes through these guards so a hostile browser
 * degrades the session instead of breaking the page.
 */
const read = (store, key) => {
  try {
    return store.getItem(key);
  } catch (e) {
    return null;
  }
};

const write = (store, key, value) => {
  try {
    store.setItem(key, value);
  } catch (e) {
    // Storage unavailable or full - the session stays in memory only.
  }
};

const drop = (store, key) => {
  try {
    store.removeItem(key);
  } catch (e) {
    // Nothing to do: the key is already unreachable.
  }
};

class AuthStorageService {
  /**
   * @returns {boolean} true when the user checked "Se souvenir de moi"
   */
  isRemembered() {
    return read(window.localStorage, REMEMBER_KEY) === 'true';
  }

  /**
   * Record the choice before the tokens are written, so they land in the
   * right store. Moves any session already in flight to that store.
   * @param {boolean} remember
   * @param {string} [email] remembered for the next visit when checked
   */
  setRemembered(remember, email) {
    const previous = SESSION_KEYS.map((key) => [key, this.getItem(key)]);

    if (remember) {
      write(window.localStorage, REMEMBER_KEY, 'true');
      if (email) {
        write(window.localStorage, REMEMBERED_EMAIL_KEY, email);
      }
    } else {
      drop(window.localStorage, REMEMBER_KEY);
      drop(window.localStorage, REMEMBERED_EMAIL_KEY);
    }

    previous.forEach(([key, value]) => {
      if (value !== null) {
        this.setItem(key, value);
      }
    });
  }

  /**
   * Persist the email to prefill on the next visit. Called after a
   * successful login so a mistyped address is never remembered.
   * @param {string} email
   */
  setRememberedEmail(email) {
    if (this.isRemembered() && email) {
      write(window.localStorage, REMEMBERED_EMAIL_KEY, email);
    }
  }

  /**
   * @returns {string} the email to prefill, empty when nothing is remembered
   */
  getRememberedEmail() {
    return this.isRemembered()
      ? read(window.localStorage, REMEMBERED_EMAIL_KEY) || ''
      : '';
  }

  /**
   * @returns {Storage} the store the current preference points at
   */
  activeStore() {
    return this.isRemembered() ? window.localStorage : window.sessionStorage;
  }

  /**
   * Reads the active store first, then falls back to the other one so a
   * session opened under the previous preference is still resolved.
   */
  getItem(key) {
    const active = read(this.activeStore(), key);
    if (active !== null) {
      return active;
    }

    const fallback = this.isRemembered()
      ? window.sessionStorage
      : window.localStorage;

    return read(fallback, key);
  }

  /**
   * Writes to the active store and clears the other, so a key never exists
   * in both at once.
   */
  setItem(key, value) {
    const remembered = this.isRemembered();
    write(remembered ? window.localStorage : window.sessionStorage, key, value);
    drop(remembered ? window.sessionStorage : window.localStorage, key);
  }

  removeItem(key) {
    drop(window.localStorage, key);
    drop(window.sessionStorage, key);
  }

  /**
   * Drops the session from both stores. The "Se souvenir de moi" preference
   * and the remembered email survive: they are what the next visit restores.
   */
  clearSession() {
    SESSION_KEYS.forEach((key) => this.removeItem(key));
  }
}

export default new AuthStorageService();
